// Visitor tracking - page views, scroll depth, form steps, time on page.
// Saved to our backend + mirrored to ad pixels (GA4/Ads/Taboola/Whop).
import axios from "axios";
import { useEffect, useRef } from "react";
import { trackPixelEvent } from "./pixels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const getVisitorId = () => {
  let id = localStorage.getItem("tmu_visitor_id");
  if (!id) {
    id = uuid();
    localStorage.setItem("tmu_visitor_id", id);
  }
  return id;
};

export const getSessionId = () => {
  let id = sessionStorage.getItem("tmu_session_id");
  if (!id) {
    id = uuid();
    sessionStorage.setItem("tmu_session_id", id);
  }
  return id;
};

const buildPayload = (eventType, data = {}) => ({
  visitor_id: getVisitorId(),
  session_id: getSessionId(),
  page: window.location.pathname || "/",
  event_type: eventType,
  data,
});

export const track = (eventType, data = {}, opts = {}) => {
  try {
    const payload = buildPayload(eventType, data);
    axios.post(`${API}/track`, payload).catch(() => {});
    // Mirror to ad platforms (GA4, Google Ads, Taboola, Whop) unless opted out.
    // High-volume auto-capture events (clicks/fields) are DB-only to avoid
    // polluting ad-platform analytics.
    if (opts.pixel !== false) {
      trackPixelEvent(eventType, { ...data, page: payload.page });
    }
  } catch (e) {
    // tracking must never break the app
  }
};

// Reliable delivery on tab close / navigation (uses sendBeacon)
export const trackBeacon = (eventType, data = {}) => {
  try {
    const payload = buildPayload(eventType, data);
    const blob = new Blob([JSON.stringify(payload)], {
      type: "application/json",
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/track`, blob);
    } else {
      axios.post(`${API}/track`, payload).catch(() => {});
    }
  } catch (e) {
    // never break the app
  }
};

/* ---------- UTM / ad attribution capture ----------
   First-touch attribution stored per visitor (tmu_utm) + last ad-click touch
   (tmu_last_touch), re-attributed whenever the visitor returns via a new ad.
   Sent once as an "attribution" event and attached to submissions. */
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_whop",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "ttclid",
  "msclkid",
  "wacid",
  "waid",
  "tblci",
  "taboola_click_id",
  "funnel_entry",
];

// Keys whose presence means "this visit came from an ad click"
const AD_KEYS = [
  "utm_source",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "ttclid",
  "msclkid",
  "wacid",
  "waid",
  "utm_whop",
  "tblci",
];

const getCookie = (name) => {
  try {
    const m = document.cookie.match(
      new RegExp("(?:^|; )" + name + "=([^;]*)")
    );
    return m ? decodeURIComponent(m[1]) : "";
  } catch {
    return "";
  }
};

const LAST_TOUCH_KEY = "tmu_last_touch";

export const captureAttribution = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v;
    });
    const isAdClick = AD_KEYS.some((k) => params.get(k));
    const wuid = getCookie("_wuid"); // Whop visitor id, if present
    const landingUrl = window.location.href;

    // ----- last touch: refresh on every ad click -----
    if (isAdClick) {
      try {
        localStorage.setItem(
          LAST_TOUCH_KEY,
          JSON.stringify({
            ...found,
            referrer: document.referrer || "",
            landing_url: landingUrl,
            at: new Date().toISOString(),
          })
        );
      } catch (e) {}
    }

    // ----- first touch: only set once -----
    const stored = localStorage.getItem("tmu_utm");
    if (Object.keys(found).length > 0) {
      if (!stored) {
        const attribution = {
          ...found,
          utm_source:
            found.utm_source ||
            (found.gclid || found.gbraid || found.wbraid
              ? "google"
              : found.fbclid
              ? "meta"
              : found.tblci || found.taboola_click_id
              ? "taboola"
              : found.utm_whop
              ? "whop"
              : "unknown"),
          whop_visitor_id: wuid || undefined,
          landing_page: window.location.pathname,
          landing_url: landingUrl,
          referrer: document.referrer || "",
          captured_at: new Date().toISOString(),
        };
        localStorage.setItem("tmu_utm", JSON.stringify(attribution));
        track("attribution", attribution);
      } else {
        // returning visitor arriving via a (new) campaign link
        track("attribution_revisit", { ...found, whop_visitor_id: wuid || undefined });
      }
    } else if (!stored) {
      const attribution = {
        utm_source: "direct",
        whop_visitor_id: wuid || undefined,
        landing_page: window.location.pathname,
        landing_url: landingUrl,
        referrer: document.referrer || "",
        captured_at: new Date().toISOString(),
      };
      localStorage.setItem("tmu_utm", JSON.stringify(attribution));
      track("attribution", attribution);
    }
  } catch (e) {
    // never break the app
  }
};

export const getStoredUTM = () => {
  try {
    const first = JSON.parse(localStorage.getItem("tmu_utm") || "null");
    const last = JSON.parse(localStorage.getItem(LAST_TOUCH_KEY) || "null");
    if (!first && !last) return null;
    return { ...(first || {}), last_touch: last || null };
  } catch {
    return null;
  }
};

/* Calendly-format UTM object for initInlineWidget({ utm }), so funnel-booked
   calls carry their ad source into Calendly (invitee.tracking). Prefers the
   most recent ad-click (last_touch) then first-touch. Returns null if none. */
export const getCalendlyUtm = () => {
  try {
    const u = getStoredUTM();
    if (!u) return null;
    const last = u.last_touch || {};
    const pick = (k) => last[k] || u[k] || "";
    const out = {};
    const src = pick("utm_source");
    if (src && src !== "direct") out.utmSource = src;
    if (pick("utm_medium")) out.utmMedium = pick("utm_medium");
    if (pick("utm_campaign")) out.utmCampaign = pick("utm_campaign");
    if (pick("utm_content")) out.utmContent = pick("utm_content");
    if (pick("utm_term")) out.utmTerm = pick("utm_term");
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
};

/* ---------- Time-on-page tracking ----------
   Accumulates only the time the tab is actually visible.
   Sends cumulative totals (backend keeps the max per session/page),
   via 20s heartbeats + a final beacon when the tab hides/closes. */
let timeTrackingStarted = false;

export const initTimeTracking = () => {
  if (timeTrackingStarted) return;
  timeTrackingStarted = true;

  let visibleSince = document.visibilityState === "visible" ? Date.now() : null;
  let accumulated = 0; // ms, per current page
  let currentPage = window.location.pathname || "/";

  const totalSeconds = () => {
    const live = visibleSince ? Date.now() - visibleSince : 0;
    return Math.round((accumulated + live) / 1000);
  };

  const send = (useBeacon) => {
    const seconds = totalSeconds();
    if (seconds <= 0) return;
    const data = { seconds, page_name: currentPage };
    if (useBeacon) trackBeacon("time_on_page", data);
    else track("time_on_page", data);
  };

  // SPA route changes: flush time for previous page, reset counter
  const checkPageChange = () => {
    const path = window.location.pathname || "/";
    if (path !== currentPage) {
      send(false);
      currentPage = path;
      accumulated = 0;
      visibleSince = document.visibilityState === "visible" ? Date.now() : null;
    }
  };

  setInterval(() => {
    checkPageChange();
    send(false); // heartbeat with cumulative total (backend takes max)
  }, 20000);

  window.addEventListener("popstate", checkPageChange);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      if (visibleSince) {
        accumulated += Date.now() - visibleSince;
        visibleSince = null;
      }
      send(true);
    } else {
      checkPageChange();
      visibleSince = Date.now();
    }
  });

  window.addEventListener("pagehide", () => {
    if (visibleSince) {
      accumulated += Date.now() - visibleSince;
      visibleSince = null;
    }
    send(true);
  });
};

// Hook: tracks page_view on mount + scroll depth milestones (25/50/75/100%)
export const usePageTracking = (pageName) => {
  const firedRef = useRef({});

  useEffect(() => {
    firedRef.current = {};
    track("page_view", { page_name: pageName });

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = Math.min(
        100,
        Math.round((window.scrollY / scrollable) * 100)
      );
      [0, 25, 50, 75, 100].forEach((m) => {
        if (pct >= m && !firedRef.current[m]) {
          firedRef.current[m] = true;
          track("scroll_depth", { page_name: pageName, percent: m });
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    // check once in case page is short / already scrolled
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [pageName]);
};


/* ---------- Global auto-capture: every visitor interaction ----------
   Delegated listeners that record ALL clicks and form-field interactions to
   our DB, so the backend has a full behavioural trail without instrumenting
   each element. PII-safe: never stores raw text/email/tel/password values —
   only the field identity + whether it was filled (choice values are safe). */

const DASHBOARD_PATH = "/nzfsqnqqyljkvs-8f3a2c";
let autoTrackStarted = false;

const _describeEl = (el) => {
  const tag = (el.tagName || "").toLowerCase();
  const testid = el.getAttribute("data-testid") || "";
  let text = (el.innerText || el.textContent || el.value || "").trim();
  if (text.length > 80) text = text.slice(0, 80);
  const desc = { tag, testid, text };
  const role = el.getAttribute("role");
  if (role) desc.role = role;
  if (tag === "a") {
    const href = el.getAttribute("href") || "";
    desc.href = href;
    try {
      const u = new URL(href, window.location.origin);
      desc.outbound = u.origin !== window.location.origin;
    } catch {}
  }
  const id = el.id;
  if (id) desc.id = id;
  const nm = el.getAttribute("name");
  if (nm) desc.name = nm;
  return desc;
};

const SENSITIVE_TYPES = ["text", "email", "tel", "password", "number", "search", "url"];

export const initAutoTrack = () => {
  if (autoTrackStarted || typeof document === "undefined") return;
  autoTrackStarted = true;

  const onDashboard = () => (window.location.pathname || "").startsWith(DASHBOARD_PATH);

  // De-dupe identical clicks fired within 400ms (e.g. bubbling / double handlers)
  let lastClickSig = "";
  let lastClickAt = 0;

  document.addEventListener(
    "click",
    (e) => {
      if (onDashboard()) return;
      const target = e.target && e.target.closest
        ? e.target.closest(
            "button, a, [role='button'], [data-testid], input[type='submit'], input[type='button'], label, [onclick]"
          )
        : null;
      if (!target) return;
      const desc = _describeEl(target);
      const sig = `${desc.tag}|${desc.testid}|${desc.text}|${desc.href || ""}`;
      const now = Date.now();
      if (sig === lastClickSig && now - lastClickAt < 400) return;
      lastClickSig = sig;
      lastClickAt = now;
      track("interaction_click", desc, { pixel: false });
    },
    true
  );

  // Field engagement: focus + change (not per keystroke). Values only for
  // non-sensitive fields (radios/checkboxes/selects); text fields store only
  // whether they were filled + length.
  const fieldDesc = (el) => {
    const tag = (el.tagName || "").toLowerCase();
    const type = (el.getAttribute && el.getAttribute("type")) || tag;
    const d = {
      field: el.getAttribute("name") || el.getAttribute("data-testid") || el.id || "",
      field_type: type,
      tag,
    };
    const ph = el.getAttribute && el.getAttribute("placeholder");
    if (ph) d.placeholder = ph.slice(0, 60);
    return d;
  };

  document.addEventListener(
    "focusin",
    (e) => {
      if (onDashboard()) return;
      const el = e.target;
      if (!el || !["input", "textarea", "select"].includes((el.tagName || "").toLowerCase())) return;
      track("field_focus", fieldDesc(el), { pixel: false });
    },
    true
  );

  document.addEventListener(
    "change",
    (e) => {
      if (onDashboard()) return;
      const el = e.target;
      const tag = (el.tagName || "").toLowerCase();
      if (!["input", "textarea", "select"].includes(tag)) return;
      const d = fieldDesc(el);
      const type = (el.getAttribute("type") || tag).toLowerCase();
      if (tag === "select") {
        d.value = (el.value || "").slice(0, 80);
      } else if (type === "checkbox" || type === "radio") {
        d.checked = !!el.checked;
        d.value = (el.value || "").slice(0, 80);
      } else if (SENSITIVE_TYPES.includes(type)) {
        d.filled = !!(el.value && el.value.length);
        d.length = el.value ? el.value.length : 0;
      } else {
        d.value = (el.value || "").slice(0, 80);
      }
      track("field_change", d, { pixel: false });
    },
    true
  );

  // Copy / paste (useful signal on funnels), and tab blur
  document.addEventListener("copy", () => {
    if (!onDashboard()) track("text_copy", { page: window.location.pathname }, { pixel: false });
  });
};
